import { useState, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar as CalendarIcon, Check } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { useLanguage } from '@/context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Booking() {
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [date, setDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [customerData, setCustomerData] = useState({ name: '', phone: '', email: '', licensePlate: '', carModel: '', notes: '' });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const { lang, t, serviceName } = useLanguage();

  useEffect(() => { loadServices(); }, []);
  useEffect(() => { if (date && selectedServices.length > 0) loadAvailableSlots(); }, [date, selectedServices]);

  const loadServices = async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/services`); setServices(r.data); } catch (e) { toast.error('Error'); }
  };

  const loadAvailableSlots = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/bookings/available-slots`, { params: { date: format(date, 'yyyy-MM-dd'), service_ids: selectedServices.join(',') } });
      setAvailableSlots(r.data.slots);
    } catch (e) { toast.error('Error'); }
  };

  const toggleService = (id) => setSelectedServices(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleSubmit = async () => {
    if (!customerData.name || !customerData.phone || !date || !selectedSlot || selectedServices.length === 0) {
      toast.error(t('booking', 'fillAll')); return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/bookings`, {
        customer_name: customerData.name, customer_phone: customerData.phone, customer_email: customerData.email,
        service_ids: selectedServices, date: format(date, 'yyyy-MM-dd'), time_slot: selectedSlot, notes: customerData.notes
      });
      setBookingResult(response.data);
      
      // Get WhatsApp confirmation link for customer
      try {
        const waResp = await axios.get(`${BACKEND_URL}/api/bookings/${response.data.id}/whatsapp?target=customer`);
        setBookingResult(prev => ({ ...prev, whatsapp_link: waResp.data.whatsapp_link }));
      } catch (e) {}
      
      setSuccess(true);
      toast.success(t('booking', 'successToast'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('booking', 'errorToast'));
    } finally { setLoading(false); }
  };

  const dateLocale = lang === 'en' ? enUS : it;

  if (success) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center" data-testid="booking-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-green-600" /></div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>{t('booking', 'successTitle')}</h1>
            <p className="text-lg text-gray-600 mb-8">{t('booking', 'successMsg')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {bookingResult?.whatsapp_link && (
                <a href={bookingResult.whatsapp_link} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white px-6" data-testid="whatsapp-confirm-button">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.468l4.571-1.453A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.15 0-4.14-.69-5.762-1.86l-.413-.278-2.715.863.885-2.633-.305-.448A9.708 9.708 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
                    {lang === 'en' ? 'Confirm on WhatsApp' : 'Conferma su WhatsApp'}
                  </Button>
                </a>
              )}
              <Button onClick={() => window.location.href = '/'} className="bg-[#0066b2] hover:bg-[#005494]" data-testid="back-to-home-button">{t('booking', 'backHome')}</Button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8" data-testid="booking-page">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>{t('booking', 'title')}</h1>
            <p className="text-lg text-gray-600">{t('booking', 'subtitle')}</p>
          </div>

          <div className="flex items-center justify-center mb-12" data-testid="booking-steps">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= s ? 'bg-[#0066b2] text-white' : 'bg-gray-200 text-gray-500'}`}>{s}</div>
                {s < 3 && <div className={`w-16 h-1 ${step > s ? 'bg-[#0066b2]' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-3xl border-2 border-gray-200 p-8 md:p-12">
            {step === 1 && (
              <div data-testid="booking-step-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('booking', 'step1Title')}</h2>
                <div className="space-y-4 mb-8">
                  {services.map((service) => (
                    <div key={service.id} className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${selectedServices.includes(service.id) ? 'border-[#0066b2] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} onClick={() => toggleService(service.id)} data-testid={`service-option-${service.id}`}>
                      <div className="flex items-start">
                        <Checkbox checked={selectedServices.includes(service.id)} className="mt-1" />
                        <div className="ml-4 flex-1">
                          <h3 className="font-bold text-gray-900">{serviceName(service.name)}</h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{service.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-[#0066b2] font-semibold">{service.price_range}</span>
                            <span className="text-gray-500">{service.duration_minutes} {t('services', 'min')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <Button onClick={() => setStep(2)} disabled={selectedServices.length === 0} className="w-full bg-[#0066b2] hover:bg-[#005494]" data-testid="next-to-step-2">{t('booking', 'continue')}</Button>
              </div>
            )}

            {step === 2 && (
              <div data-testid="booking-step-2">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('booking', 'step2Title')}</h2>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'date')}</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="date-picker-trigger">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, 'PPP', { locale: dateLocale }) : t('booking', 'selectDate')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date() || d.getDay() === 0 || d.getDay() === 6} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
                {date && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'availableTime')}</label>
                    <div className="grid grid-cols-3 gap-3">
                      {availableSlots.length === 0 ? (
                        <div className="col-span-3 text-center text-gray-500 py-4" data-testid="no-slots-available">{t('booking', 'noSlots')}</div>
                      ) : availableSlots.map((slot) => (
                        <button key={slot} onClick={() => setSelectedSlot(slot)} className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${selectedSlot === slot ? 'border-[#0066b2] bg-blue-50 text-[#0066b2]' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`} data-testid={`time-slot-${slot}`}>{slot}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-4">
                  <Button onClick={() => setStep(1)} variant="outline" className="flex-1" data-testid="back-to-step-1">{t('booking', 'back')}</Button>
                  <Button onClick={() => setStep(3)} disabled={!date || !selectedSlot} className="flex-1 bg-[#0066b2] hover:bg-[#005494]" data-testid="next-to-step-3">{t('booking', 'continue')}</Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="booking-step-3">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk' }}>{t('booking', 'step3Title')}</h2>
                <div className="space-y-4 mb-8">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'fullName')}</label><Input value={customerData.name} onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })} placeholder="Mario Rossi" data-testid="customer-name-input" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'phone')}</label><Input value={customerData.phone} onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })} placeholder="+39 123 456 7890" data-testid="customer-phone-input" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'email')}</label><Input type="email" value={customerData.email} onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })} placeholder="mario.rossi@email.com" data-testid="customer-email-input" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'plate')}</label><Input value={customerData.licensePlate} onChange={(e) => setCustomerData({ ...customerData, licensePlate: e.target.value })} placeholder="AB123CD" data-testid="customer-plate-input" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'carModel')}</label><Input value={customerData.carModel} onChange={(e) => setCustomerData({ ...customerData, carModel: e.target.value })} placeholder="Fiat Panda" data-testid="customer-car-model-input" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">{t('booking', 'notes')}</label><Textarea value={customerData.notes} onChange={(e) => setCustomerData({ ...customerData, notes: e.target.value })} placeholder={t('booking', 'notesPlaceholder')} rows={4} data-testid="customer-notes-input" /></div>
                </div>
                <div className="flex gap-4">
                  <Button onClick={() => setStep(2)} variant="outline" className="flex-1" data-testid="back-to-step-2">{t('booking', 'back')}</Button>
                  <Button onClick={handleSubmit} disabled={loading} className="flex-1 bg-[#0066b2] hover:bg-[#005494]" data-testid="submit-booking-button">
                    {loading ? t('booking', 'booking') : t('booking', 'confirmBooking')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
